import { useState } from "react";
import { API } from "../lib/api";
import { Box, Button, CircularProgress, Collapse, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip, Typography } from "@mui/material";
import { ExpandLess, ExpandMore, Folder, UploadFile } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";

const OpportunityFileUploader = ({ opportunityId, onUploadSuccess, opportunity, setOpportunity, setMasterFeedback, fullWidth = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setFeedback(null);
  };

  const handleFileClick = (key) => {
    API.Files.GetSignedUrl(key)
      .then((res) => {
        if (res.status !== 200) throw new Error("Unable to open file");
        const link = document.createElement("a");
        link.href = res.data.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
  
        // ✅ delay click slightly
        setTimeout(() => {
          link.click();
          document.body.removeChild(link);
        }, 100);
      })
      .catch((err) => {
        setMasterFeedback({ open: true, message: "Failed to open file...", type: "error" });
        console.error("Failed to open file:", err);
      });
  };

  const handleDeleteFile = async (fileKey) => {
    try {
      await API.Files.DeleteOpportunityFile(opportunityId, fileKey);
      
      setMasterFeedback({open: true, message: "File Removed Successfully!", type: "success"})
      setOpportunity((prev) => ({
        ...prev,
        files: prev.files.filter((key) => key !== fileKey),
      }));
    } catch (err) {
      console.error("Failed to delete file", err);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !opportunityId) {
      setFeedback("Missing file or opportunity ID");
      return;
    }
  
    const formData = new FormData();
    formData.append("file", selectedFile);
  
    try {
      setUploading(true);
      const res = await API.Files.UploadOpportunityFile(opportunityId, formData);
      setFeedback("File uploaded successfully");
  
      // ✅ Add uploaded file key to local UI
      setOpportunity(prev => ({
        ...prev,
        files: [...(prev?.files || []), res.data.key]
      }));
  
      onUploadSuccess?.(res.data.key);
    } catch (err) {
      setFeedback("Upload failed");
      console.error(err);
    } finally {
      setUploading(false);
      setSelectedFile(null);
    }
  };

  return (
            <div className={fullWidth ? "" : "col-12 col-md-8 col-lg-8 col-xl-6"}>
                <div className="card">
                    <div className="card-header">
                        {/* LEFT: Icon and title inline */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                onClick={() => setCollapsed(!collapsed)}
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                {collapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>
                            <h5 className="mb-0">Files - <span>{opportunity?.name}</span></h5>
                        </Box>
                    </div>
                    <Collapse in={!collapsed}>
                        <div className="card-body" style={{maxHeight: 323, minHeight: 323, overflowY: "scroll"}}>
                        <Box sx={{ margin: "1em 0" }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                <Tooltip title="To enable this feature contact your system administrator" describeChild>
                                <span tabIndex={0} aria-label="Choose File unavailable" style={{ display: "inline-flex" }}>
                                <Button
                                variant="outlined"
                                component="label"
                                disabled
                                startIcon={<UploadFile />}
                                >
                                Choose File
                                <input type="file" hidden disabled onChange={handleFileChange} />
                                </Button>
                                </span>
                                </Tooltip>
                                <Typography variant="body2">
                                {selectedFile ? selectedFile.name : "No file selected"}
                                </Typography>
                                <Button
                                variant="contained"
                                disabled={uploading || !selectedFile}
                                onClick={handleUpload}
                                >
                                {uploading ? <CircularProgress size={20} /> : "Upload"}
                                </Button>
                            </Stack>
                            {feedback && (
                                <Typography sx={{ mt: 1 }} color={feedback.includes("successfully") ? "success.main" : "error"}>
                                {feedback}
                                </Typography>
                            )}
                            </Box>                       
                        {/* Below should be list of files */}
                        <List dense={true}>
                        {opportunity?.files?.map((file) => (
                          <ListItem
                            key={file}
                            disablePadding
                            secondaryAction={
                              <IconButton
                                edge="end"
                                onClick={(e) => {
                                  e.stopPropagation(); // ⛔ prevent triggering ListItemButton tap
                                  handleDeleteFile(file);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemButton onClick={() => handleFileClick(file)}>
                              <ListItemIcon>
                                <Folder />
                              </ListItemIcon>
                              <ListItemText
                                primary={file.split("/").pop()}
                                sx={{ color: "#4680ff" }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                        </List>
                        </div>
                    </Collapse>
                </div>
            </div>
  );
};

export default OpportunityFileUploader;
